import { queryOptions } from '@tanstack/react-query'
import {
  CountryListModel,
  CountryListResponse,
} from 'src/models/country.models'
import { getCountries } from 'src/queries/countries/countries.network'

export const countriesQueryKeys = {
  all: () => ['countries'] as const,
  list: (locale: string | undefined) =>
    [...countriesQueryKeys.all(), 'list', locale] as const,
}

export const countriesQueries = {
  list: (locale: string | undefined) =>
    queryOptions({
      queryKey: countriesQueryKeys.list(locale),
      queryFn: () => getCountries(locale!),
      enabled: Boolean(locale),
      // staleTime/gcTime Infinity: countries are static reference data
      // (country labels + address field metadata) that only change with an
      // Addressulator deploy. The Redux slice this replaces fetched once and
      // kept the list in the store until logout; matching that here avoids
      // refetch churn from the ~7 consumers that mount this query. The locale
      // is part of the key, so a locale change naturally fetches fresh data
      // instead of relying on staleness.
      staleTime: Infinity,
      gcTime: Infinity,
    }),
}

/**
 * Pure `select` helpers mirroring the legacy reselect transforms
 * (`selectCountryListModelsFromState` / `selectCountryOptions`). Defined at
 * module level so `useQuery({ ...countriesQueries.list(locale), select })`
 * keeps a stable function reference and React Query memoizes the result.
 */
export const toCountryListModelMap = (
  countries: CountryListResponse[],
): Record<CountryListResponse['iso'], CountryListModel> =>
  countries.reduce<Record<CountryListResponse['iso'], CountryListModel>>(
    (acc, country) => {
      acc[country.iso] = new CountryListModel(country)
      return acc
    },
    {},
  )

export const toCountryOptions = (
  countries: CountryListResponse[],
): { value: string; details: { text: string } }[] =>
  countries.map((country) => ({
    value: country.iso,
    details: {
      text: country.label,
    },
  }))
